import type { OrderRecord } from '@forq/orders-data';
import {
  bookedIncomingIn,
  computeFinanceKpis,
  computeVatReport,
  invoiceBookingDate,
  isInPeriod,
  type FinancePeriod,
} from './financeAnalytics';
import { isInvoiceOverdue, type Invoice } from './types';
import type { IncomingInvoice } from './incoming/types';

/**
 * Periodenabschluss for a PREPARATORY finance module. FORQ does not book — the Fibu does — so a
 * hard lock would be theatre: it could not stop the real ledger. What actually matters is that the
 * figures handed over stay the figures of record. So closing FREEZES a snapshot, and any later
 * change to the period surfaces as a drift against it. That catches the real risk (someone
 * back-dating a document into a month already given to the accountant) instead of pretending.
 */
export interface ClosureSnapshot {
  readonly netRevenue: number;
  readonly expense: number;
  /** Rohertrag — see FinanceKpis.grossProfit; deliberately not called a result. */
  readonly grossProfit: number;
  readonly vatPayable: number;
  readonly outgoingCount: number;
  readonly incomingCount: number;
}

export interface PeriodClosure {
  readonly id: string;
  readonly tenantId: string;
  readonly from: string;
  readonly to: string;
  readonly closedAt: string;
  readonly closedBy: string;
  readonly note: string;
  readonly snapshot: ClosureSnapshot;
}

export interface ClosePeriodInput {
  readonly tenantId: string;
  readonly period: FinancePeriod;
  readonly closedBy: string;
  readonly note?: string;
  readonly snapshot: ClosureSnapshot;
}

export interface PeriodClosingGateway {
  listClosures(tenantId: string): Promise<readonly PeriodClosure[]>;
  closePeriod(input: ClosePeriodInput): Promise<PeriodClosure>;
  reopenPeriod(tenantId: string, closureId: string): Promise<void>;
}

/** The figures frozen at closing time — the same basis every other Finance screen reads. */
export function buildClosureSnapshot(
  invoices: readonly Invoice[],
  incoming: readonly IncomingInvoice[],
  period: FinancePeriod,
): ClosureSnapshot {
  const kpis = computeFinanceKpis(invoices, incoming, period);
  const vat = computeVatReport(invoices, incoming, period);
  return {
    netRevenue: kpis.revenue,
    expense: kpis.expense,
    grossProfit: kpis.grossProfit,
    vatPayable: vat.payable,
    outgoingCount: vat.outgoingCount,
    incomingCount: vat.incomingCount,
  };
}

export type ClosingCheckState = 'ok' | 'open' | 'info';

export interface ClosingCheck {
  readonly key: string;
  readonly label: string;
  readonly detail: string;
  readonly state: ClosingCheckState;
  readonly count: number;
  readonly route: string;
}

/**
 * The preconditions for handing a period over. Only 'open' checks block the closure — overdue
 * receivables and the VAT figure are shown for the eye, not as gates: a customer paying late is
 * no reason to keep a month open.
 */
export function computeClosingChecks(
  orders: readonly OrderRecord[],
  invoices: readonly Invoice[],
  incoming: readonly IncomingInvoice[],
  period: FinancePeriod,
  todayIsoDate: string,
): readonly ClosingCheck[] {
  const inPeriod = (invoice: Invoice) => isInPeriod(invoiceBookingDate(invoice), period);

  const readyToBill = orders.filter((order) => order.status === 'billing-ready');
  const unfinished = invoices.filter((invoice) =>
    inPeriod(invoice)
    && ['draft', 'in_review', 'approved'].includes(invoice.documentStatus));
  const unsent = invoices.filter((invoice) => inPeriod(invoice) && invoice.documentStatus === 'invoiced');
  const uncoded = incoming.filter((invoice) => isInPeriod(invoice.invoiceDate, period) && invoice.status === 'captured');
  const overdue = invoices.filter((invoice) => isInvoiceOverdue(invoice, todayIsoDate));
  const bookedIncoming = bookedIncomingIn(incoming, period);

  return [
    {
      key: 'billing',
      label: 'Vorgänge abgerechnet',
      detail: readyToBill.length === 0
        ? 'Kein Vorgang wartet auf eine Rechnung.'
        : `${readyToBill.length} Vorgänge sind bereit zur Abrechnung, aber noch nicht abgerechnet.`,
      state: readyToBill.length === 0 ? 'ok' : 'open',
      count: readyToBill.length,
      route: '/finance/abrechnung',
    },
    {
      key: 'drafts',
      label: 'Keine offenen Rechnungsentwürfe',
      detail: unfinished.length === 0
        ? 'Alle Rechnungen im Zeitraum tragen eine Rechnungsnummer.'
        : `${unfinished.length} Rechnungen sind noch Entwurf, in Prüfung oder freigegeben.`,
      state: unfinished.length === 0 ? 'ok' : 'open',
      count: unfinished.length,
      route: '/finance/abrechnung',
    },
    {
      key: 'unsent',
      label: 'Rechnungen versendet',
      detail: unsent.length === 0
        ? 'Alle erstellten Rechnungen sind raus.'
        : `${unsent.length} Rechnungen sind erstellt, aber noch nicht versendet.`,
      state: unsent.length === 0 ? 'ok' : 'open',
      count: unsent.length,
      route: '/finance/abrechnung',
    },
    {
      key: 'uncoded',
      label: 'Eingangsrechnungen kontiert',
      detail: uncoded.length === 0
        ? 'Alle Eingangsrechnungen im Zeitraum sind kontiert.'
        : `${uncoded.length} Eingangsrechnungen sind erfasst, aber nicht kontiert – sie fehlen sonst im Buchungsstapel.`,
      state: uncoded.length === 0 ? 'ok' : 'open',
      count: uncoded.length,
      route: '/finance/eingangsrechnungen',
    },
    {
      key: 'stack',
      label: 'Buchungsstapel vollständig',
      detail: `${bookedIncoming.length} kontierte Eingangsrechnungen im Zeitraum gehen an die Fibu.`,
      state: 'info',
      count: bookedIncoming.length,
      route: '/finance/buchhaltung',
    },
    {
      key: 'overdue',
      label: 'Überfällige Forderungen',
      detail: overdue.length === 0
        ? 'Keine überfälligen Forderungen offen.'
        : `${overdue.length} Forderungen sind überfällig – kein Hindernis für den Abschluss, aber fürs Mahnwesen.`,
      state: 'info',
      count: overdue.length,
      route: '/finance/mahnwesen',
    },
  ];
}

export function canClosePeriod(checks: readonly ClosingCheck[]): boolean {
  return checks.every((check) => check.state !== 'open');
}

export function openCheckCount(checks: readonly ClosingCheck[]): number {
  return checks.filter((check) => check.state === 'open').length;
}

export interface ClosureDriftRow {
  readonly label: string;
  readonly frozen: number;
  readonly current: number;
  readonly delta: number;
}

/**
 * Compares a closure's frozen snapshot against what the period looks like NOW. A non-zero delta
 * means something changed after the handover — the one thing an accountant needs to hear about.
 */
export function computeClosureDrift(closure: PeriodClosure, current: ClosureSnapshot): readonly ClosureDriftRow[] {
  const rows: readonly { label: string; key: keyof ClosureSnapshot }[] = [
    { label: 'Umsatz (netto)', key: 'netRevenue' },
    { label: 'Aufwand (netto)', key: 'expense' },
    { label: 'Rohertrag', key: 'grossProfit' },
    { label: 'USt-Zahllast', key: 'vatPayable' },
    { label: 'Ausgangsbelege', key: 'outgoingCount' },
    { label: 'Eingangsbelege', key: 'incomingCount' },
  ];
  return rows.map(({ label, key }) => {
    const frozen = closure.snapshot[key];
    const value = current[key];
    return { label, frozen, current: value, delta: Math.round((value - frozen) * 100) / 100 };
  });
}

export function hasDrift(drift: readonly ClosureDriftRow[]): boolean {
  return drift.some((row) => row.delta !== 0);
}

/** The period a closure covers, as the shared FinancePeriod shape. */
export function closurePeriod(closure: PeriodClosure): FinancePeriod {
  return { from: closure.from, to: closure.to };
}
